import React, { useState, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { db, storage } from "../firebase";
import { collection, query, where, getDocs, addDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { message } from "antd";

const STEPS = [
  { id: "eligibility", label: "Éligibilité" },
  { id: "identity",    label: "Identité" },
  { id: "documents",   label: "Documents" },
  { id: "email",       label: "Email" },
  { id: "tutorial",    label: "Tutoriel" },
  { id: "review",      label: "Confirmation" },
];

export default function LateAccessRequest() {
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [dark, setDark] = useState(() => localStorage.getItem("ws-theme") === "dark");

  // Eligibility
  const [hoursOk, setHoursOk] = useState(null);
  const [isStudent, setIsStudent] = useState(null);
  const [tutorialDone, setTutorialDone] = useState(null);
  const [hasSubscription, setHasSubscription] = useState(null);
  const [eligChecked, setEligChecked] = useState(false);
  const [eligLoading, setEligLoading] = useState(false);
  const [totalHours, setTotalHours] = useState(0);

  // Identity
  const [hasStudentCard, setHasStudentCard] = useState(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [uniEmail, setUniEmail] = useState("");

  // Documents
  const [cinFile, setCinFile] = useState(null);
  const [cinPreview, setCinPreview] = useState(null);
  const [studentCardFile, setStudentCardFile] = useState(null);
  const [studentCardPreview, setStudentCardPreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Email OTP
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);

  // Tutorial
  const [tutorialChecked, setTutorialChecked] = useState(false);
  const [quizPassed, setQuizPassed] = useState(false);

  // Submission
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const cinRef = useRef();
  const cardRef = useRef();

  const toggleTheme = () => {
    setDark(d => {
      localStorage.setItem("ws-theme", !d ? "dark" : "light");
      return !d;
    });
  };

  const t = dark ? theme.dark : theme.light;

  // ── Step 0: Check eligibility ─────────────────────────────
  const checkEligibility = async () => {
    setEligLoading(true);
    try {
      const uid = user?.uid;
      if (!uid) throw new Error("Non connecté");
      const twoMonthsAgo = new Date();
      twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 2);
      const snap = await getDocs(query(
        collection(db, "visiteur"),
        where("FirebaseId", "==", uid),
        where("startTime", ">=", twoMonthsAgo)
      ));
      let hours = 0;
      snap.forEach(doc => { hours += Number(doc.data().hours || 0); });
      setTotalHours(Math.round(hours * 10) / 10);
      setHoursOk(hours >= 35);
    } catch (err) {
      console.error(err);
      message.error("Erreur lors de la vérification.");
    }
    setEligChecked(true);
    setEligLoading(false);
  };

  const canProceedEligibility = hoursOk && isStudent === true && tutorialDone === true && hasSubscription !== null;
  const canProceedIdentity = hasStudentCard === true
    ? (cinFile && studentCardFile)
    : (cinFile && firstName.trim() && lastName.trim() && uniEmail.trim() && uniEmail.includes("@"));

  // ── File helpers ──────────────────────────────────────────
  const handleFile = (file, setter, previewSetter) => {
    if (!file) return;
    setter(file);
    const reader = new FileReader();
    reader.onload = e => previewSetter(e.target.result);
    reader.readAsDataURL(file);
  };

  // ── Upload ────────────────────────────────────────────────
  const uploadFiles = async () => {
    setUploading(true);
    try {
      const uid = user?.uid || "anonymous";
      const ts = Date.now();
      const cinRef2 = ref(storage, `late_access/${uid}/cin_${ts}`);
      await uploadBytes(cinRef2, cinFile);
      const cinUrl = await getDownloadURL(cinRef2);
      let cardUrl = null;
      if (studentCardFile) {
        const cardRef2 = ref(storage, `late_access/${uid}/card_${ts}`);
        await uploadBytes(cardRef2, studentCardFile);
        cardUrl = await getDownloadURL(cardRef2);
      }
      setUploading(false);
      return { cinUrl, cardUrl };
    } catch (err) {
      setUploading(false);
      throw err;
    }
  };

  // ── Submit ────────────────────────────────────────────────
  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const { cinUrl, cardUrl } = await uploadFiles();
      await addDoc(collection(db, "late_access_requests"), {
        userId: user?.uid || null,
        userEmail: user?.email || uniEmail,
        firstName: firstName || null,
        lastName: lastName || null,
        uniEmail: hasStudentCard ? null : uniEmail,
        hasStudentCard,
        hasSubscription,
        cinUrl,
        studentCardUrl: cardUrl,
        totalHours,
        tutorialCompleted: true,
        quizPassed: true,
        status: "pending",
        createdAt: new Date(),
      });
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      message.error("Erreur lors de la soumission.");
    }
    setSubmitting(false);
  };

  // ── Sub-components ────────────────────────────────────────
  const StepDot = ({ i }) => (
    <div style={{
      width: i === step ? 28 : 8, height: 8, borderRadius: 4,
      background: i < step ? t.green : i === step ? t.blue : t.border,
      transition: "all 0.3s", flexShrink: 0,
    }} />
  );

  const NextBtn = ({ onClick, label = "Continuer →", disabled = false }) => (
    <button onClick={onClick} disabled={disabled}
      style={{ ...s.primaryBtn, background: disabled ? t.bg3 : t.blue, color: disabled ? t.text3 : "#fff", opacity: disabled ? 0.6 : 1, marginTop: 8 }}>
      {label}
    </button>
  );

  const BackBtn = ({ onClick }) => (
    <button onClick={onClick} style={{ ...s.outlineBtn, background: t.bg3, border: `1px solid ${t.border}`, color: t.text2, marginBottom: 16 }}>
      ← Retour
    </button>
  );

  const FileUpload = ({ label, file, preview, inputRef, onChange, required }) => (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: t.text2, marginBottom: 6 }}>
        {label} {required && <span style={{ color: t.red }}>*</span>}
      </div>
      <input type="file" accept="image/*,application/pdf" ref={inputRef} style={{ display: "none" }} onChange={e => onChange(e.target.files[0])} />
      {preview ? (
        <div style={{ position: "relative" }}>
          <img src={preview} alt="" style={{ width: "100%", borderRadius: 10, border: `1px solid ${t.border}`, maxHeight: 160, objectFit: "cover" }} />
          <button onClick={() => { onChange(null); if (inputRef.current) inputRef.current.value = ""; }}
            style={{ position: "absolute", top: 8, right: 8, background: t.redBg, border: `1px solid ${t.redBorder}`, color: t.red, borderRadius: 8, padding: "3px 8px", cursor: "pointer", fontSize: 12 }}>
            ✕ Supprimer
          </button>
        </div>
      ) : (
        <button onClick={() => inputRef.current?.click()}
          style={{ width: "100%", border: `2px dashed ${t.border}`, background: t.bg3, borderRadius: 12, padding: "20px 14px", cursor: "pointer", color: t.text2, fontSize: 13, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 24 }}>📎</span>
          <span>Cliquez pour choisir un fichier</span>
          <span style={{ fontSize: 11, color: t.text3 }}>Image ou PDF</span>
        </button>
      )}
    </div>
  );

  // ── Success screen ────────────────────────────────────────
  if (submitted) {
    return (
      <div style={{ background: t.bg, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ maxWidth: 420, width: "100%", padding: "0 24px", textAlign: "center" }}>
          <div style={{ width: 80, height: 80, borderRadius: "50%", background: t.greenBg, border: `2px solid ${t.green}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 36, margin: "0 auto 24px" }}>✅</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: t.text1, marginBottom: 10 }}>Demande envoyée !</div>
          <div style={{ fontSize: 14, color: t.text2, lineHeight: 1.7, marginBottom: 24 }}>
            Votre dossier a été transmis à l'administration WeStudy.<br />
            Si votre demande est approuvée, vous serez contacté pour <strong>prendre vos empreintes digitales</strong> sur place.
          </div>
          <div style={{ background: t.amberBg, border: `1px solid ${t.amberBorder}`, borderRadius: 12, padding: "12px 16px", fontSize: 12, color: t.amberText, marginBottom: 24 }}>
            ⏳ Le délai de traitement est généralement de 2 à 5 jours ouvrables.
          </div>
          <button onClick={() => window.history.back()}
            style={{ ...s.outlineBtn, background: t.bg3, border: `1px solid ${t.border}`, color: t.text2, padding: "10px 24px", fontSize: 13 }}>
            ← Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: t.bg, minHeight: "100vh" }}>
      <div style={{ maxWidth: 480, margin: "0 auto", paddingBottom: 60 }}>

        {/* Topbar */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 18px 0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <img src="/logo.PNG" alt="WeStudy" style={{ height: 36, objectFit: "contain" }} onError={e => e.target.style.display = "none"} />
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: t.text1 }}>Accès nocturne</div>
              <div style={{ fontSize: 11, color: t.text2 }}>Demande d'accès tardif</div>
            </div>
          </div>
          <button onClick={toggleTheme} style={{ ...s.toggleTrack, background: t.bg3, border: `1px solid ${t.border}` }}>
            <div style={{ ...s.toggleKnob, background: t.blue, transform: dark ? "translateX(16px)" : "translateX(0)" }} />
          </button>
        </div>

        {/* Progress */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "16px 18px 0", justifyContent: "center" }}>
          {STEPS.map((_, i) => <StepDot key={i} i={i} />)}
        </div>
        <div style={{ textAlign: "center", fontSize: 11, color: t.text3, marginTop: 6 }}>
          Étape {step + 1} sur {STEPS.length} · {STEPS[step].label}
        </div>

        <div style={{ padding: "20px 18px 0" }}>

          {/* ── STEP 0: Eligibility ── */}
          {step === 0 && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: t.text3, marginBottom: 16 }}>Conditions d'éligibilité</div>

              {/* Info card */}
              <div style={{ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 14, padding: 16, marginBottom: 16 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: t.text1, marginBottom: 12 }}>Pour accéder aux horaires tardifs, vous devez :</div>
                {[
                  { icon: "⏱️", text: "Avoir cumulé au moins 35 heures de présence durant les 2 derniers mois" },
                  { icon: "🎓", text: "Être étudiant(e) en université ou école supérieure" },
                  { icon: "🪪", text: "Disposer d'un abonnement WeStudy actif (ou dépôt de garantie de 10 dt pour les passages journée)" },
                  { icon: "📋", text: "Avoir complété le tutoriel d'accès nocturne" },
                ].map((item, i) => (
                  <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: i < 3 ? 10 : 0 }}>
                    <span style={{ fontSize: 18, flexShrink: 0 }}>{item.icon}</span>
                    <span style={{ fontSize: 13, color: t.text2, lineHeight: 1.5 }}>{item.text}</span>
                  </div>
                ))}
              </div>

              {/* Hours check */}
              <div style={{ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 14, padding: 16, marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: t.text1, marginBottom: 10 }}>⏱️ Vérifier mes heures (2 derniers mois)</div>
                {!eligChecked ? (
                  <button onClick={checkEligibility} disabled={eligLoading}
                    style={{ ...s.primaryBtn, background: t.blue, padding: "10px" }}>
                    {eligLoading ? "Vérification..." : "Vérifier automatiquement"}
                  </button>
                ) : (
                  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: hoursOk ? t.greenBg : t.redBg, borderRadius: 10 }}>
                    <span style={{ fontSize: 20 }}>{hoursOk ? "✅" : "❌"}</span>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: hoursOk ? t.green : t.red }}>
                        {hoursOk
                          ? `${totalHours}h sur les 2 derniers mois — Condition remplie ✅`
                          : `Seulement ${totalHours}h sur 2 mois — 35h requis`}
                      </div>
                      {!hoursOk && <div style={{ fontSize: 11, color: t.text2, marginTop: 2 }}>Continuez à fréquenter WeStudy pour atteindre les 35h.</div>}
                    </div>
                  </div>
                )}
              </div>

              {/* Student */}
              <div style={{ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 14, padding: 16, marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: t.text1, marginBottom: 10 }}>🎓 Êtes-vous étudiant(e) en université ?</div>
                <div style={{ display: "flex", gap: 10 }}>
                  <ChoiceBtn label="Oui" active={isStudent === true} color={t.green} bg={t.greenBg} onClick={() => setIsStudent(true)} t={t} />
                  <ChoiceBtn label="Non" active={isStudent === false} color={t.red} bg={t.redBg} onClick={() => setIsStudent(false)} t={t} />
                </div>
                {isStudent === false && <div style={{ fontSize: 12, color: t.red, marginTop: 8 }}>⚠️ L'accès nocturne est réservé aux étudiants universitaires.</div>}
              </div>

              {/* Subscription */}
              <div style={{ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 14, padding: 16, marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: t.text1, marginBottom: 10 }}>🪪 Disposez-vous d'un abonnement WeStudy actif ?</div>
                <div style={{ display: "flex", gap: 10, marginBottom: 8 }}>
                  <ChoiceBtn label="Oui" active={hasSubscription === true} color={t.green} bg={t.greenBg} onClick={() => setHasSubscription(true)} t={t} />
                  <ChoiceBtn label="Non" active={hasSubscription === false} color={t.amber} bg={t.amberBg} onClick={() => setHasSubscription(false)} t={t} />
                </div>
                {hasSubscription === false && (
                  <div style={{ background: t.amberBg, border: `1px solid ${t.amberBorder}`, borderRadius: 10, padding: "10px 12px", fontSize: 12, color: t.amberText, lineHeight: 1.6 }}>
                    ⚠️ Sans abonnement actif, un <strong>dépôt de garantie de 10 dt</strong> est requis à l'accueil avant toute utilisation de l'accès nocturne en passage journée.
                  </div>
                )}
                {hasSubscription === true && (
                  <div style={{ background: t.greenBg, borderRadius: 10, padding: "8px 12px", fontSize: 12, color: t.green }}>
                    ✅ Abonnement actif — aucun dépôt requis.
                  </div>
                )}
              </div>

              {/* Tutorial done */}
              <div style={{ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 14, padding: 16, marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: t.text1, marginBottom: 6 }}>📋 Avez-vous complété le tutoriel d'accès nocturne ?</div>
                <div style={{ fontSize: 12, color: t.text2, marginBottom: 10 }}>
                  Si non, <a href="/late-tutorial" style={{ color: t.blue, fontWeight: 600 }}>cliquez ici pour le faire maintenant →</a>
                </div>
                <div style={{ display: "flex", gap: 10 }}>
                  <ChoiceBtn label="Oui, je l'ai fait" active={tutorialDone === true} color={t.green} bg={t.greenBg} onClick={() => setTutorialDone(true)} t={t} />
                  <ChoiceBtn label="Pas encore" active={tutorialDone === false} color={t.amber} bg={t.amberBg} onClick={() => setTutorialDone(false)} t={t} />
                </div>
                {tutorialDone === false && <div style={{ fontSize: 12, color: t.amberText, marginTop: 8 }}>Vous devez d'abord compléter le tutoriel avant de faire cette demande.</div>}
              </div>

              <NextBtn onClick={() => setStep(1)} disabled={!canProceedEligibility} label="Je remplis les conditions →" />
            </div>
          )}

          {/* ── STEP 1: Identity ── */}
          {step === 1 && (
            <div>
              <BackBtn onClick={() => setStep(0)} />
              <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: t.text3, marginBottom: 16 }}>Identité</div>

              <div style={{ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 14, padding: 16, marginBottom: 16 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: t.text1, marginBottom: 6 }}>Possédez-vous une carte d'étudiant ?</div>
                <div style={{ fontSize: 12, color: t.text2, marginBottom: 12 }}>Si non, une adresse email universitaire avec votre nom sera requise.</div>
                <div style={{ display: "flex", gap: 10 }}>
                  <ChoiceBtn label="✅ Oui" active={hasStudentCard === true} color={t.green} bg={t.greenBg} onClick={() => setHasStudentCard(true)} t={t} />
                  <ChoiceBtn label="❌ Non" active={hasStudentCard === false} color={t.amber} bg={t.amberBg} onClick={() => setHasStudentCard(false)} t={t} />
                </div>
              </div>

              {hasStudentCard === false && (
                <div style={{ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 14, padding: 16, marginBottom: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: t.text1, marginBottom: 12 }}>Vos informations</div>
                  {[
                    { label: "Prénom *", value: firstName, setter: setFirstName, placeholder: "Votre prénom" },
                    { label: "Nom *",    value: lastName,  setter: setLastName,  placeholder: "Votre nom de famille" },
                    { label: "Email universitaire *", value: uniEmail, setter: setUniEmail, placeholder: "prenom.nom@univ.tn", type: "email" },
                  ].map(field => (
                    <div key={field.label} style={{ marginBottom: 12 }}>
                      <label style={{ fontSize: 12, fontWeight: 600, color: t.text2, display: "block", marginBottom: 5 }}>{field.label}</label>
                      <input type={field.type || "text"} value={field.value} onChange={e => field.setter(e.target.value)} placeholder={field.placeholder}
                        style={{ width: "100%", height: 42, borderRadius: 10, border: `1px solid ${t.border}`, background: t.bg3, color: t.text1, padding: "0 12px", fontSize: 13, outline: "none", boxSizing: "border-box" }} />
                    </div>
                  ))}
                  <div style={{ fontSize: 11, color: t.text2, background: t.blueBg, padding: "8px 12px", borderRadius: 8 }}>
                    ℹ️ L'adresse email doit contenir votre prénom et nom pour valider votre identité étudiante.
                  </div>
                </div>
              )}

              <NextBtn onClick={() => setStep(2)} disabled={hasStudentCard === null} />
            </div>
          )}

          {/* ── STEP 2: Documents ── */}
          {step === 2 && (
            <div>
              <BackBtn onClick={() => setStep(1)} />
              <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: t.text3, marginBottom: 16 }}>Documents justificatifs</div>

              <div style={{ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 14, padding: 16, marginBottom: 16 }}>
                <FileUpload label="📷 Carte d'identité nationale (CIN)" file={cinFile} preview={cinPreview} inputRef={cinRef} required onChange={f => handleFile(f, setCinFile, setCinPreview)} />
                {hasStudentCard && (
                  <FileUpload label="🎓 Carte d'étudiant" file={studentCardFile} preview={studentCardPreview} inputRef={cardRef} required onChange={f => handleFile(f, setStudentCardFile, setStudentCardPreview)} />
                )}
                <div style={{ fontSize: 11, color: t.text3, marginTop: 4 }}>
                  🔒 Vos documents sont stockés de manière sécurisée et utilisés uniquement pour la vérification.
                </div>
              </div>

              <NextBtn onClick={() => setStep(3)} disabled={!canProceedIdentity} />
            </div>
          )}

          {/* ── STEP 3: Email ── */}
          {step === 3 && (
            <div>
              <BackBtn onClick={() => setStep(2)} />
              <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: t.text3, marginBottom: 16 }}>Vérification email</div>

              <div style={{ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 14, padding: 20, marginBottom: 16, textAlign: "center" }}>
                <div style={{ fontSize: 36, marginBottom: 12 }}>📧</div>
                <div style={{ fontSize: 15, fontWeight: 600, color: t.text1, marginBottom: 8 }}>Vérification par email</div>
                <div style={{ fontSize: 13, color: t.text2, lineHeight: 1.6, marginBottom: 20 }}>
                  Un code de vérification sera envoyé à<br />
                  <strong style={{ color: t.text1 }}>{hasStudentCard === false ? uniEmail : user?.email}</strong>
                </div>
                {!otpSent ? (
                  <>
                    <div style={{ background: t.amberBg, border: `1px solid ${t.amberBorder}`, borderRadius: 10, padding: "10px 14px", fontSize: 12, color: t.amberText, marginBottom: 16 }}>
                      ⚙️ L'envoi d'OTP sera disponible prochainement.
                    </div>
                    <button onClick={() => { setOtpSent(true); setOtpVerified(true); }}
                      style={{ ...s.primaryBtn, background: t.blue, padding: "10px" }}>
                      Ignorer pour l'instant →
                    </button>
                  </>
                ) : (
                  <div style={{ display: "flex", alignItems: "center", gap: 10, background: t.greenBg, borderRadius: 10, padding: "10px 14px", justifyContent: "center" }}>
                    <span style={{ fontSize: 18 }}>✅</span>
                    <span style={{ fontSize: 13, fontWeight: 500, color: t.green }}>Email validé</span>
                  </div>
                )}
              </div>

              <NextBtn onClick={() => setStep(4)} disabled={!otpVerified} />
            </div>
          )}

          {/* ── STEP 4: Tutorial ── */}
          {step === 4 && (
            <div>
              <BackBtn onClick={() => setStep(3)} />
              <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: t.text3, marginBottom: 16 }}>Tutoriel & Quiz</div>

              <div style={{ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 14, padding: 20, marginBottom: 12 }}>
                <div style={{ fontSize: 36, marginBottom: 12, textAlign: "center" }}>📋</div>
                <div style={{ fontSize: 15, fontWeight: 600, color: t.text1, marginBottom: 8 }}>Tutoriel d'accès nocturne</div>
                <div style={{ fontSize: 13, color: t.text2, lineHeight: 1.6, marginBottom: 16 }}>
                  Ce tutoriel couvre les règles de conduite, les procédures d'urgence et les consignes de sécurité pour l'accès tardif à WeStudy.
                </div>
                <a href="/late-tutorial" style={{ display: "block", textAlign: "center", padding: "12px", background: t.blueBg, border: `1px solid ${t.blue}`, borderRadius: 10, color: t.blue, fontSize: 13, fontWeight: 600, textDecoration: "none", marginBottom: 16 }}>
                  📖 Accéder au tutoriel →
                </a>
                <div style={{ height: 1, background: t.border, marginBottom: 16 }} />
                <div style={{ fontSize: 13, fontWeight: 500, color: t.text1, marginBottom: 10 }}>Avez-vous complété le tutoriel et le quiz ?</div>
                <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", padding: "10px 12px", background: tutorialChecked ? t.greenBg : t.bg3, borderRadius: 10, border: `1px solid ${tutorialChecked ? t.green : t.border}`, transition: "all 0.15s" }}>
                  <input type="checkbox" checked={tutorialChecked} onChange={e => { setTutorialChecked(e.target.checked); setQuizPassed(e.target.checked); }}
                    style={{ width: 18, height: 18, cursor: "pointer", accentColor: t.green }} />
                  <span style={{ fontSize: 13, color: t.text1 }}>J'ai lu et compris le tutoriel et j'ai réussi le quiz</span>
                </label>
              </div>

              <NextBtn onClick={() => setStep(5)} disabled={!tutorialChecked} />
            </div>
          )}

          {/* ── STEP 5: Review ── */}
          {step === 5 && (
            <div>
              <BackBtn onClick={() => setStep(4)} />
              <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: t.text3, marginBottom: 16 }}>Récapitulatif</div>

              <div style={{ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 14, padding: 16, marginBottom: 16 }}>
                {[
                  { label: "Heures (2 derniers mois)", value: `${totalHours}h ✅` },
                  { label: "Statut étudiant",          value: "Confirmé ✅" },
                  { label: "Abonnement",               value: hasSubscription ? "Actif ✅" : "Dépôt garantie 10 dt ✅" },
                  { label: "Tutoriel",                  value: "Complété ✅" },
                  { label: "Carte d'identité",          value: cinFile ? `${cinFile.name} ✅` : "—" },
                  { label: hasStudentCard ? "Carte étudiant" : "Email uni.",
                    value: hasStudentCard ? (studentCardFile ? `${studentCardFile.name} ✅` : "—") : `${uniEmail} ✅` },
                  { label: "Vérification email",        value: "Validée ✅" },
                ].map(row => (
                  <div key={row.label} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${t.border}` }}>
                    <span style={{ fontSize: 12, color: t.text2 }}>{row.label}</span>
                    <span style={{ fontSize: 12, fontWeight: 500, color: t.text1, maxWidth: 200, textAlign: "right", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.value}</span>
                  </div>
                ))}
              </div>

              {/* Deposit reminder */}
              {hasSubscription === false && (
                <div style={{ background: t.amberBg, border: `1px solid ${t.amberBorder}`, borderRadius: 14, padding: 16, marginBottom: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: t.amberText, marginBottom: 6 }}>💰 Rappel — Dépôt de garantie</div>
                  <div style={{ fontSize: 12, color: t.amberText, lineHeight: 1.6 }}>
                    Sans abonnement actif, vous devrez déposer <strong>10 dt de garantie</strong> à l'accueil WeStudy avant votre premier accès nocturne. Ce montant est restitué à la fin de votre utilisation.
                  </div>
                </div>
              )}

              {/* Next steps */}
              <div style={{ background: t.blueBg, border: `1px solid ${t.border}`, borderRadius: 14, padding: 16, marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: t.blueText, marginBottom: 10 }}>📌 Prochaines étapes</div>
                {[
                  "L'administration WeStudy examinera votre dossier",
                  "Délai de traitement : 2 à 5 jours ouvrables",
                  "Si approuvé, vous serez convoqué pour enregistrer vos empreintes digitales",
                  "En cas de refus, un email vous expliquera les raisons",
                ].map((item, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, marginBottom: i < 3 ? 8 : 0 }}>
                    <span style={{ fontSize: 11, color: t.blueText, fontWeight: 700, flexShrink: 0 }}>{i + 1}.</span>
                    <span style={{ fontSize: 12, color: t.blueText, lineHeight: 1.5 }}>{item}</span>
                  </div>
                ))}
              </div>

              <button onClick={handleSubmit} disabled={submitting}
                style={{ ...s.primaryBtn, background: submitting ? t.bg3 : t.green, color: submitting ? t.text3 : "#fff", padding: "14px", fontSize: 14, opacity: submitting ? 0.7 : 1 }}>
                {submitting ? "Envoi en cours..." : "✅ Soumettre ma demande"}
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

function ChoiceBtn({ label, active, color, bg, onClick, t }) {
  return (
    <button onClick={onClick}
      style={{ flex: 1, padding: "10px 8px", borderRadius: 10, border: `1.5px solid ${active ? color : t.border}`, background: active ? bg : t.bg3, color: active ? color : t.text2, fontSize: 13, fontWeight: active ? 600 : 400, cursor: "pointer", transition: "all 0.15s" }}>
      {label}
    </button>
  );
}

const theme = {
  light: {
    bg: "#f8fafc", bg3: "#f1f5f9", border: "#e2e8f0",
    text1: "#0f172a", text2: "#64748b", text3: "#94a3b8",
    blue: "#2563eb", blueBg: "#eff6ff", blueText: "#1d4ed8",
    green: "#16a34a", greenBg: "#f0fdf4",
    amber: "#d97706", amberBg: "#fffbeb", amberText: "#b45309", amberBorder: "#fde68a",
    red: "#dc2626", redBg: "#fef2f2", redBorder: "#fecaca",
    cardBg: "#ffffff",
  },
  dark: {
    bg: "#0f172a", bg3: "#1a2740", border: "#273549",
    text1: "#f1f5f9", text2: "#94a3b8", text3: "#475569",
    blue: "#378ADD", blueBg: "#1e3a5f", blueText: "#85B7EB",
    green: "#4ade80", greenBg: "#052e16",
    amber: "#fbbf24", amberBg: "#2a1e07", amberText: "#fcd34d", amberBorder: "#78350f",
    red: "#f87171", redBg: "#2d1515", redBorder: "#7f1d1d",
    cardBg: "#1e293b",
  },
};

const s = {
  toggleTrack: { width: 36, height: 20, borderRadius: 20, cursor: "pointer", position: "relative", flexShrink: 0 },
  toggleKnob: { width: 14, height: 14, borderRadius: "50%", position: "absolute", top: 2, left: 2, transition: "transform 0.2s" },
  outlineBtn: { fontSize: 12, borderRadius: 8, padding: "6px 10px", cursor: "pointer" },
  primaryBtn: { width: "100%", border: "none", borderRadius: 12, fontSize: 13, fontWeight: 600, cursor: "pointer", transition: "all 0.15s" },
};